import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type CreatePatientProfileRequest, GET_PATIENT_PROFILES } from '../../contracts';
import { createPatientProfile } from '../api/patients';

/**
 * Mutation variables for the first-person create.
 *
 * `TTracking` stays OPAQUE to this package even though the shape now lives here.
 * `PatientCreatedEventProps` moved to
 * `../../contracts/patient-find-or-link-analytics.types` at openspec task 2.1b,
 * so this hook COULD name it — and deliberately does not. What decides that is
 * the EMIT, not the type: `patient_created` has no emit site in a patients root
 * (it fires from `src/hooks/mutation/useMutateCreatePatientProfile.ts`), so the
 * name is undeclared and this hook's only job is to round-trip the bag verbatim
 * to the `onCreated` port that emits it. A generic keeps the caller's type at the
 * call site instead of degrading it to `Record<string, unknown>` and casting it
 * back; binding it to the payload type here would make this hook a participant
 * in an event it does not own.
 */
export type CreateOwnPatientProfileArgs<TTracking = unknown> = {
  patientProfile: CreatePatientProfileRequest;
  /** Passed as a query param for session direct-share permission check.
   *  When a session is directly shared, the sharee can process (create/update/link/unlink)
   *  patient profiles on that session by providing its ID here. */
  sessionId?: string;
  /**
   * Optional analytics metadata forwarded verbatim to `onCreated`. Carries
   * `surface`, `match_method` (create-flavour enum), `result_count_seen`,
   * `time_to_outcome_ms`, `search_id`, and `feature_flag_variant` for the
   * find-or-link combobox funnel; legacy callers omit it.
   */
  tracking?: TTracking;
};

export interface CreateOwnPatientProfileResult {
  createPatientProfileResponse: Awaited<ReturnType<typeof createPatientProfile>>;
}

/**
 * The FIRST-PERSON half of `useMutateCreatePatientProfile` (D2.2, ARCFE-1053).
 *
 * `POST /patient-profiles/` is patients'; `POST /teams/{teamId}/members/
 * {memberId}/patient-profiles/` is workspace's and its hook
 * (`useMutateCreateTeamMemberPatientProfile`) stayed in the app hub, as did the
 * review-mode branch that picks between them.
 *
 * THE INVALIDATION IS THE EVIDENCE for that split: the legacy create hook
 * invalidated BOTH caches — but as two explicit, separate calls under two
 * different key constants, never one prefix catching the other. Two identities,
 * two owners. If a patients write ever has to invalidate the member cache by
 * PREFIX instead, they share an identity and collapse back to one family.
 *
 * PORT, not a toast: the success toast, the Rematch analytics dispatch and the
 * `patient_created` Snowplow event are app reactions (packages/CLAUDE.md).
 * `onCreated` fires with the response and the variables so the delegate can
 * build byte-identical copy and event props.
 *
 * SIDE-EFFECT ORDER: legacy fired the toast BEFORE this invalidation and the
 * two analytics emissions after it; all three now fire after, inside the port,
 * in their original order relative to each other. Nothing observes the
 * difference — the toast and the analytics emissions touch no cache, and they
 * run in the same synchronous `onSuccess` tick either way.
 */
export function useMutateCreateOwnPatientProfile<TTracking = unknown>({
  isViewingOtherSession = false,
  onCreated,
  onCreateFailed,
}: {
  /** `isViewingOtherSessionAtom`, app-resolved (cohort-C). */
  isViewingOtherSession?: boolean;
  onCreated?: (
    result: CreateOwnPatientProfileResult,
    input: CreateOwnPatientProfileArgs<TTracking>
  ) => void;
  /**
   * Fires for EVERY failure. The legacy hook toasted only for an `AxiosError`
   * (it wanted the backend's field-level validation detail); that narrowing is
   * the delegate's, because `extractAxiosErrorDetail` and the copy are both
   * app-side.
   */
  onCreateFailed?: (error: unknown) => void;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: CreateOwnPatientProfileArgs<TTracking>
    ): Promise<CreateOwnPatientProfileResult> => {
      const createPatientProfileResponse = await createPatientProfile(
        input.patientProfile,
        isViewingOtherSession ? input.sessionId : undefined
      );
      return { createPatientProfileResponse };
    },
    onSuccess: (result, input) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_PATIENT_PROFILES],
      });
      onCreated?.(result, input);
    },
    onError: (error) => {
      onCreateFailed?.(error);
    },
  });
}
